import React, { useState } from 'react';
import AnalyticsCharts from '../components/AnalyticsCharts';
// Phase Audit Fix C: AIInsightsPanel, LeadScoringPanel, ROIDashboard,
// CallRecordingPlayer, AILearningEngine were demo components rendering
// hardcoded scores / fake insights / no-op buttons. Removed from this
// surface. The data-backed analytics tabs (charts, phase 2-6) remain.
import { BarChart3, Brain, TrendingUp, Activity, Download, Sparkles } from 'lucide-react';
import { PageHeader } from '../components/ui/page-header';
import { TabBar } from '../components/ui/tab-bar';

// Phase 2 Analytics Components
import ASRPerformanceHeatmap from '../components/analytics/ASRPerformanceHeatmap';
import VariantComparison from '../components/analytics/VariantComparison';
import SentimentTrendline from '../components/analytics/SentimentTrendline';
import CampaignLeaderboard from '../components/analytics/CampaignLeaderboard';

// Phase 3 Analytics Components
import IntentSankey from '../components/analytics/IntentSankey';
import ConversationPathHeatmap from '../components/analytics/ConversationPathHeatmap';
import LiveWordCloud from '../components/analytics/LiveWordCloud';
import TimelineReplay from '../components/analytics/TimelineReplay';

// Phase 4 Analytics Components (Ops & Health)
import SystemHealthGauges from '../components/analytics/SystemHealthGauges';
import CarrierQualityHeatmap from '../components/analytics/CarrierQualityHeatmap';
import SmartAlertsPanel from '../components/analytics/SmartAlertsPanel';
import LiveCallsPanel from '../components/analytics/LiveCallsPanel';

// Phase 5 Analytics Components (Share & Insights)
import ExportCenter from '../components/analytics/ExportCenter';
import AIDailyReportCard from '../components/analytics/AIDailyReportCard';
import PermalinkButton from '../components/analytics/PermalinkButton';
import ReducedMotionToggle from '../components/analytics/ReducedMotionToggle';

// Phase 6 Analytics Components (Predictive Intelligence)
import TrendForecast from '../components/analytics/TrendForecast';
import AnomalyPredictor from '../components/analytics/AnomalyPredictor';
import PredictiveHeatmap from '../components/analytics/PredictiveHeatmap';
import IntentDrillDown from '../components/analytics/IntentDrillDown';
import KeywordSentimentMatrix from '../components/analytics/KeywordSentimentMatrix';
import TopicClusters from '../components/analytics/TopicClusters';
import TranscriptExporter from '../components/analytics/TranscriptExporter';

// Phase 6 Category C: Agent Performance Deep Dive
import AgentPerformanceMatrix from '../components/analytics/AgentPerformanceMatrix';
import ConversationQualityScorecard from '../components/analytics/ConversationQualityScorecard';
import AgentBehaviorTimeline from '../components/analytics/AgentBehaviorTimeline';

// Phase 6 Category D: Custom Alerting & Webhooks
import CustomAlertBuilder from '../components/analytics/CustomAlertBuilder';
import WebhookManager from '../components/analytics/WebhookManager';
import AlertHistory from '../components/analytics/AlertHistory';

// Phase 6 Category E: Multi-dimensional Filters
import AdvancedFilterBuilder from '../components/analytics/AdvancedFilterBuilder';

// Phase 6 Category F: API Playground
import APIPlayground from '../components/analytics/APIPlayground';

// Phase 6 Category G: Automation & Recommendations
import ScheduleRecommendations from '../components/analytics/ScheduleRecommendations';
import VolumeBalancer from '../components/analytics/VolumeBalancer';

// Phase 6 Category H: Unified Reporting
import MultiCampaignAggregator from '../components/analytics/MultiCampaignAggregator';
import CustomKPIDashboard from '../components/analytics/CustomKPIDashboard';
import BrandedReportExporter from '../components/analytics/BrandedReportExporter';

// Phase 6 Category I: Integrations & APIs
import SlackIntegration from '../components/analytics/SlackIntegration';
import CRMSyncManager from '../components/analytics/CRMSyncManager';

// Phase 6 Category J: Security & Governance
import AuditTrailViewer from '../components/analytics/AuditTrailViewer';
import ExportPermissions from '../components/analytics/ExportPermissions';
import MFASetup from '../components/analytics/MFASetup';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('charts');
  const [dashboardFilters, setDashboardFilters] = useState({
    days: 14,
    campaign_id: null
  });
  const [reducedMotion, setReducedMotion] = useState(() => {
    // Check user preference
    const saved = localStorage.getItem('reduced_motion');
    return saved === 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  const handleFilterChange = (newFilters) => {
    setDashboardFilters(prev => ({ ...prev, ...newFilters }));
  };

  const tabs = [
    { id: 'charts', label: 'Charts & Trends', icon: BarChart3 },
    { id: 'phase2', label: 'Quality & Performance', icon: TrendingUp },
    { id: 'phase3', label: 'Conversation Visuals', icon: Brain },
    { id: 'phase4', label: 'Ops & Health', icon: Activity },
    { id: 'phase5', label: 'Share & Insights', icon: Download },
    { id: 'phase6', label: 'Predictive Intelligence', icon: Sparkles },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Insights"
        title="Analytics & Intelligence"
        description="Cross-cutting view of calls, conversion, sentiment, and quality."
      />

      <TabBar
        value={activeTab}
        onChange={setActiveTab}
        items={tabs.map((t) => ({ id: t.id, label: t.label }))}
      />

      {/* Tab Content */}
      <div>
        {activeTab === 'charts' && <AnalyticsCharts />}
        {activeTab === 'phase2' && (
          <div className="space-y-6">
            {/* ASR Performance & Variant Comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ASRPerformanceHeatmap 
                onFilterChange={handleFilterChange}
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <VariantComparison 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Sentiment Trendline */}
            <SentimentTrendline 
              filters={dashboardFilters}
              reducedMotion={reducedMotion}
            />

            {/* Campaign Leaderboard */}
            <CampaignLeaderboard 
              filters={dashboardFilters}
              reducedMotion={reducedMotion}
            />
          </div>
        )}
        {activeTab === 'phase3' && (
          <div className="space-y-6">
            {/* Intent Sankey & Conversation Path Heatmap */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <IntentSankey 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <ConversationPathHeatmap 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Live Word Cloud */}
            <LiveWordCloud 
              filters={dashboardFilters}
              reducedMotion={reducedMotion}
            />

            {/* Timeline Replay */}
            <TimelineReplay 
              filters={dashboardFilters}
              reducedMotion={reducedMotion}
            />
          </div>
        )}
        {activeTab === 'phase4' && (
          <div className="space-y-6">
            {/* System Health Gauges */}
            <SystemHealthGauges 
              reducedMotion={reducedMotion}
            />

            {/* Carrier Quality & Live Calls */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CarrierQualityHeatmap 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
                onFilterChange={handleFilterChange}
              />
              <LiveCallsPanel 
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Smart Alerts Panel (fixed position overlay) */}
            <SmartAlertsPanel 
              reducedMotion={reducedMotion}
              onScopeFilter={handleFilterChange}
            />
          </div>
        )}
        {activeTab === 'phase5' && (
          <div className="space-y-6">
            {/* Top Bar: Permalink & Reduced Motion Toggle */}
            <div className="flex items-center justify-between gap-4">
              <PermalinkButton 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <ReducedMotionToggle 
                enabled={reducedMotion}
                onChange={(value) => {
                  setReducedMotion(value);
                  localStorage.setItem('reduced_motion', value);
                }}
              />
            </div>

            {/* AI Daily Report & Export Center */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AIDailyReportCard 
                reducedMotion={reducedMotion}
              />
              <ExportCenter 
                reducedMotion={reducedMotion}
              />
            </div>
          </div>
        )}
        {activeTab === 'phase6' && (
          <div className="space-y-6">
            {/* Category A: Predictive Ops */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category A: Predictive Ops</h3>
              <p className="text-xs text-slate-400">Detect issues before they impact calls</p>
            </div>
            
            {/* Trend Forecast */}
            <TrendForecast 
              filters={dashboardFilters}
              reducedMotion={reducedMotion}
            />

            {/* Anomaly Predictor & Predictive Heatmap */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AnomalyPredictor 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <PredictiveHeatmap 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Category B: Conversational Insights 2.0 */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category B: Conversational Insights 2.0</h3>
              <p className="text-xs text-slate-400">Deeper NLP analytics and conversation understanding</p>
            </div>

            {/* Intent Drill-Down & Keyword Sentiment */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <IntentDrillDown 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <KeywordSentimentMatrix 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Topic Clusters & Transcript Exporter */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TopicClusters 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <TranscriptExporter 
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Category C: Agent Performance Deep Dive */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category C: Agent Performance Deep Dive</h3>
              <p className="text-xs text-slate-400">Individual agent analytics and quality metrics</p>
            </div>

            {/* Agent Performance Matrix & Conversation Quality */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AgentPerformanceMatrix 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <ConversationQualityScorecard 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Agent Behavior Timeline */}
            <AgentBehaviorTimeline 
              filters={dashboardFilters}
              reducedMotion={reducedMotion}
            />

            {/* Category D: Custom Alerting & Webhooks */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category D: Custom Alerting & Webhooks</h3>
              <p className="text-xs text-slate-400">Build custom alerts and manage webhook integrations</p>
            </div>

            {/* Custom Alert Builder & Webhook Manager */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CustomAlertBuilder 
                reducedMotion={reducedMotion}
              />
              <WebhookManager 
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Alert History */}
            <AlertHistory 
              reducedMotion={reducedMotion}
            />

            {/* Category E: Multi-dimensional Filters */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category E: Multi-dimensional Filters</h3>
              <p className="text-xs text-slate-400">Create complex filters that apply across all charts</p>
            </div>

            {/* Advanced Filter Builder */}
            <AdvancedFilterBuilder 
              onFiltersChange={handleFilterChange}
              reducedMotion={reducedMotion}
            />

            {/* Category F: API Playground */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category F: API Playground</h3>
              <p className="text-xs text-slate-400">Test and explore analytics API endpoints interactively</p>
            </div>

            {/* API Playground */}
            <APIPlayground 
              reducedMotion={reducedMotion}
            />

            {/* Category G: Automation & Recommendations */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category G: Automation & Recommendations</h3>
              <p className="text-xs text-slate-400">AI-powered scheduling and load balancing</p>
            </div>

            {/* Schedule Recommendations & Volume Balancer */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ScheduleRecommendations 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
              <VolumeBalancer 
                filters={dashboardFilters}
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Category H: Unified Reporting */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category H: Unified Reporting</h3>
              <p className="text-xs text-slate-400">Multi-campaign analytics and custom dashboards</p>
            </div>

            {/* Multi-Campaign Aggregator */}
            <MultiCampaignAggregator 
              reducedMotion={reducedMotion}
            />

            {/* Custom KPI Dashboard & Branded Report Exporter */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CustomKPIDashboard 
                reducedMotion={reducedMotion}
              />
              <BrandedReportExporter 
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Category I: Integrations & APIs */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category I: Integrations & APIs</h3>
              <p className="text-xs text-slate-400">Connect with external platforms</p>
            </div>

            {/* Slack Integration & CRM Sync */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SlackIntegration 
                reducedMotion={reducedMotion}
              />
              <CRMSyncManager 
                reducedMotion={reducedMotion}
              />
            </div>

            {/* Category J: Security & Governance */}
            <div className="mb-4 mt-12 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-2">Category J: Security & Governance</h3>
              <p className="text-xs text-slate-400">Audit trails and access control</p>
            </div>

            {/* Audit Trail Viewer */}
            <AuditTrailViewer 
              reducedMotion={reducedMotion}
            />

            {/* Export Permissions & MFA Setup */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ExportPermissions 
                reducedMotion={reducedMotion}
              />
              <MFASetup 
                reducedMotion={reducedMotion}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
